/* FURTAGE: klebender Kauf-Button auf der Produktseite, nur Handy (< 750px).
   - Leiste sichtbar, sobald der normale Kauf-Button aus dem Bild gescrollt ist (nach oben oder unten).
     Beim Laden, solange man den Button noch nicht erreicht hat, bleibt sie weg.
   - Tippen löst den normalen Kauf-Button aus: Warenkorb-Drawer und Fehlermeldungen bleiben die von Dawn.
   - Ladezustand/Deaktivierung wird vom normalen Button gespiegelt.
   - Solange die Leiste sichtbar ist: unten Platz auf der Seite, Google-Merchant-Widget sitzt darüber. */
(() => {
  if (customElements.get('furtage-sticky-atc')) return;

  const MOBILE_QUERY = '(max-width: 749px)';
  const WIDGET_ID = 'google-merchantwidget-iframe-wrapper';
  const WIDGET_MAX_COLLAPSED = 120; // größer = Widget ist aufgeklappt, dann nicht verschieben
  const ERROR_WINDOW_MS = 15000;
  const root = document.documentElement;

  class FurtageStickyAtc extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.formId);
      this.main = this.form ? this.form.querySelector('button[name="add"]') : null;
      this.button = this.querySelector('.fsatc__button');
      if (!this.main || !this.button) return;

      this.spinner = this.button.querySelector('.loading__spinner');
      this.header = document.querySelector('.section-header');
      const productForm = this.form.closest('product-form');
      this.errorWrapper = productForm ? productForm.querySelector('.product-form__error-message-wrapper') : null;

      this.mq = window.matchMedia(MOBILE_QUERY);
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reached = false;
      this.shown = false;
      this.ticking = false;
      this.lastBarClick = 0;
      this.widget = null;

      this.requestUpdate = this.requestUpdate.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onClick = this.onClick.bind(this);

      this.button.addEventListener('click', this.onClick);
      window.addEventListener('scroll', this.requestUpdate, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('pageshow', this.onResize);
      if (this.mq.addEventListener) this.mq.addEventListener('change', this.onResize);
      else if (this.mq.addListener) this.mq.addListener(this.onResize);

      this.mainObserver = new MutationObserver(() => {
        this.syncState();
        this.requestUpdate();
      });
      this.mainObserver.observe(this.main, { attributes: true, attributeFilter: ['class', 'disabled', 'aria-disabled'] });

      if (this.errorWrapper) {
        this.errorObserver = new MutationObserver(() => this.revealError());
        this.errorObserver.observe(this.errorWrapper, { attributes: true, attributeFilter: ['hidden'] });
      }

      this.syncState();
      this.update();
    }

    disconnectedCallback() {
      if (!this.mq) return;
      this.button.removeEventListener('click', this.onClick);
      window.removeEventListener('scroll', this.requestUpdate);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('pageshow', this.onResize);
      if (this.mq.removeEventListener) this.mq.removeEventListener('change', this.onResize);
      else if (this.mq.removeListener) this.mq.removeListener(this.onResize);
      if (this.mainObserver) this.mainObserver.disconnect();
      if (this.errorObserver) this.errorObserver.disconnect();
      if (this.widgetObserver) this.widgetObserver.disconnect();
      root.classList.remove('fsatc-visible');
      root.style.removeProperty('--fsatc-h');
      root.style.removeProperty('--fsatc-widget-lift');
    }

    onClick(event) {
      event.preventDefault();
      if (this.button.getAttribute('aria-disabled') === 'true') return;
      if (this.main.disabled || this.main.getAttribute('aria-disabled') === 'true') return;
      this.lastBarClick = Date.now();
      this.main.click();
    }

    // Ladezustand und Deaktivierung vom normalen Button übernehmen
    syncState() {
      const loading = this.main.classList.contains('loading');
      const busy = loading || this.main.getAttribute('aria-disabled') === 'true';
      this.button.classList.toggle('loading', loading);
      if (busy) this.button.setAttribute('aria-disabled', 'true');
      else this.button.removeAttribute('aria-disabled');
      if (this.spinner) this.spinner.classList.toggle('hidden', !loading);
      this.available = !this.main.disabled;
    }

    // Fehlermeldung erscheint beim normalen Button: nach einem Tipp auf die Leiste dorthin scrollen
    revealError() {
      if (this.errorWrapper.hidden) return;
      if (Date.now() - this.lastBarClick > ERROR_WINDOW_MS) return;
      this.lastBarClick = 0;
      this.errorWrapper.scrollIntoView({ block: 'center', behavior: this.reduceMotion.matches ? 'auto' : 'smooth' });
    }

    requestUpdate() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => this.update());
    }

    onResize() {
      this.syncState();
      this.update();
      this.syncLayout();
    }

    headerBottom() {
      if (!this.header || !this.header.isConnected) return 0;
      const bottom = this.header.getBoundingClientRect().bottom;
      return Math.max(0, Math.min(bottom, window.innerHeight));
    }

    update() {
      this.ticking = false;
      const rect = this.main.getBoundingClientRect();
      const hasBox = rect.width > 0 && rect.height > 0;
      const top = this.headerBottom();
      const onScreen = hasBox && rect.bottom > top && rect.top < window.innerHeight;
      const above = hasBox && rect.bottom <= top;
      if (onScreen || above) this.reached = true;

      this.setShown(this.mq.matches && this.available && this.reached && hasBox && !onScreen);
      // Das Google-Widget wird erst nach dem Laden eingefügt
      if (this.shown && !this.widget && document.getElementById(WIDGET_ID)) this.syncLayout();
    }

    setShown(show) {
      if (show === this.shown) return;
      this.shown = show;
      this.classList.toggle('is-visible', show);
      root.classList.toggle('fsatc-visible', show);
      this.syncLayout();
    }

    // Höhe der Leiste als Platz unten und Abstand für das Google-Widget
    syncLayout() {
      if (!this.mq.matches) {
        this.setVar('--fsatc-h', null);
        this.setVar('--fsatc-widget-lift', null);
        return;
      }
      const height = this.offsetHeight;
      this.setVar('--fsatc-h', `${height}px`);

      const widget = document.getElementById(WIDGET_ID);
      if (widget && widget !== this.widget) this.watchWidget(widget);

      let lift = 0;
      if (this.shown && widget && widget.offsetHeight <= WIDGET_MAX_COLLAPSED) {
        const bottom = parseFloat(window.getComputedStyle(widget).bottom) || 0;
        lift = Math.max(0, height - bottom);
      }
      this.setVar('--fsatc-widget-lift', `${lift}px`);
    }

    setVar(name, value) {
      if (root.style.getPropertyValue(name) === (value || '')) return;
      if (value) root.style.setProperty(name, value);
      else root.style.removeProperty(name);
    }

    watchWidget(widget) {
      if (this.widgetObserver) this.widgetObserver.disconnect();
      this.widget = widget;
      this.widgetObserver = new MutationObserver(() => this.syncLayout());
      this.widgetObserver.observe(widget, { attributes: true, attributeFilter: ['style'] });
    }
  }

  customElements.define('furtage-sticky-atc', FurtageStickyAtc);
})();
